// spawn-async-capture-with-timeout.ts
//
// Thay spawnSync cho các lệnh chạy LÂU (gitnexus wiki tới 15ph, analyze, claude
// --print). spawnSync BLOCK event loop hoàn toàn → spinner (setInterval) đông
// cứng + Ctrl-C không kill được child (zombie process ăn CPU/network).
//
// spawn async + Promise: event loop tự do (spinner tick), timeout tự kill, và
// SIGINT từ user được forward xuống child → không để lại zombie.
//
// Trả shape TƯƠNG THÍCH spawnSync (status/signal/stdout/stderr) để call site đổi
// tối thiểu. timedOut=true khi vượt timeoutMs (child đã bị kill SIGTERM).

import { type SpawnOptions, spawn } from "node:child_process";

export interface SpawnCaptureResult {
  status: number | null;
  signal: NodeJS.Signals | null;
  stdout: string;
  stderr: string;
  timedOut: boolean;
}

export interface SpawnCaptureOptions {
  cwd?: string;
  timeoutMs?: number;
  // Forward SIGINT (Ctrl-C) của process cha xuống child rồi reject. Mặc định bật.
  forwardSigint?: boolean;
}

export function spawnAsyncCapture(
  command: string,
  args: string[],
  opts: SpawnCaptureOptions = {},
): Promise<SpawnCaptureResult> {
  const { cwd, timeoutMs, forwardSigint = true } = opts;
  return new Promise<SpawnCaptureResult>((resolve, reject) => {
    const spawnOpts: SpawnOptions = { cwd, stdio: ["ignore", "pipe", "pipe"] };
    const child = spawn(command, args, spawnOpts);

    let stdout = "";
    let stderr = "";
    let timedOut = false;
    let settled = false;

    const timer = timeoutMs
      ? setTimeout(() => {
          timedOut = true;
          child.kill("SIGTERM");
        }, timeoutMs)
      : null;

    // Ctrl-C: kill child rồi để close handler resolve (signal=SIGINT).
    const onSigint = () => child.kill("SIGINT");
    if (forwardSigint) process.on("SIGINT", onSigint);

    const cleanup = () => {
      if (timer) clearTimeout(timer);
      if (forwardSigint) process.removeListener("SIGINT", onSigint);
    };

    child.stdout?.on("data", (c) => {
      stdout += c.toString();
    });
    child.stderr?.on("data", (c) => {
      stderr += c.toString();
    });

    child.on("error", (err) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err);
    });

    child.on("close", (status, signal) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve({ status, signal, stdout, stderr, timedOut });
    });
  });
}
